import { FunctionComponent } from 'preact';
import Router, { route, RoutableProps } from 'preact-router';
import { useEffect } from 'preact/hooks';
import { authStore } from './services/auth.service';
import { ToastHost } from './components/toast';
import { ConfirmHost } from './components/confirm-dialog';
import { LoginPage } from './pages/login.page';
import { ForgotPasswordPage } from './pages/forgot-password.page';
import { ForgotPasswordSentPage } from './pages/forgot-password-sent.page';
import { ResetPasswordPage } from './pages/reset-password.page';
import { ResetPasswordDonePage } from './pages/reset-password-done.page';
import { MfaSetupPage } from './pages/mfa-setup.page';
import { MfaSetupDonePage } from './pages/mfa-setup-done.page';
import { TkwPage } from './pages/tkw.page';
import { LogoutPage } from './pages/logout.page';
import { NotFoundPage } from './pages/not-found.page';

/**
 * Wraps the public landing route. Redirects authenticated users to /tkw or /mfa/setup.
 */
const HomeRedirect: FunctionComponent<RoutableProps> = () => {
  useEffect(() => {
    const user = authStore.getUser();
    if (!user) {
      route('/login', true);
      return;
    }
    route(user.mfaEnabled ? '/tkw' : '/mfa/setup', true);
  }, []);
  return null;
};

/**
 * Wraps a route in an auth guard. The guard redirects to /login when no token
 * is present, forces mfa setup when missing, and prevents users with active
 * mfa from re-entering the setup screen.
 */
function withAuth<P extends RoutableProps>(
  Component: FunctionComponent<P>,
  options: { requiresMfaEnabled?: boolean; requiresMfaSetup?: boolean } = {}
): FunctionComponent<P> {
  const Guarded: FunctionComponent<P> = (props) => {
    const user = authStore.getUser();

    useEffect(() => {
      if (!user) {
        route('/login', true);
        return;
      }
      if (options.requiresMfaEnabled && !user.mfaEnabled) {
        route('/mfa/setup', true);
        return;
      }
      if (options.requiresMfaSetup && user.mfaEnabled) {
        route('/tkw', true);
        return;
      }
    }, [user]);

    if (!user) return null;
    if (options.requiresMfaEnabled && !user.mfaEnabled) return null;
    if (options.requiresMfaSetup && user.mfaEnabled) return null;

    return <Component {...props} />;
  };
  return Guarded;
}

const GuardedMfaSetupPage = withAuth(MfaSetupPage, { requiresMfaSetup: true });
const GuardedMfaSetupDonePage = withAuth(MfaSetupDonePage);
const GuardedTkwPage = withAuth(TkwPage, { requiresMfaEnabled: true });
const GuardedLogoutPage = withAuth(LogoutPage);

export function App() {
  return (
    <>
      <Router>
        <HomeRedirect path="/" />
        <LoginPage path="/login" />
        <ForgotPasswordPage path="/forgot-password" />
        <ForgotPasswordSentPage path="/forgot-password/sent" />
        <ResetPasswordPage path="/reset-password/:id/:email/:token/:locale" />
        <ResetPasswordDonePage path="/reset-password/done" />
        <GuardedMfaSetupPage path="/mfa/setup" />
        <GuardedMfaSetupDonePage path="/mfa/setup/done" />
        <GuardedTkwPage path="/tkw" />
        <GuardedLogoutPage path="/logout" />
        <NotFoundPage default />
      </Router>
      <ToastHost />
      <ConfirmHost />
    </>
  );
}
